import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Chip } from "@/components/Chip";
import { SearchInput, Select } from "@/components/inputs";
import { products, type SpecField, type SpecGroup } from "@/api/client";
import { qk, useProductSpec } from "@/modules/products/data";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";

/** The spec-field editor — what makes "Add product" mean anything.
 *
 *  I flagged the cost when Dattatray asked for Add product: a product is a
 *  catalogue `kind`, and the SPEC TEMPLATE keyed by that id is what the Asset
 *  Catalog renders a model form from. A kind created without one shows an
 *  EMPTY form. His answer: *"Add product : build the spec-field editor too."*
 *
 *  Before 0064 there was nowhere for an edit to go — the templates were
 *  Python literals. Now they are a JSONB column, `/modules/asset-catalog/
 *  templates` reads it, and this writes it. ONE source: the literals are the
 *  seed, and `tests/db/test_kinds_are_a_table.py` keeps them honest.
 *
 *  TWO THINGS ARE PERMANENT AND THE EDITOR SAYS SO RATHER THAN HIDING IT:
 *
 *    the product id    five foreign keys point at it, and every tool sends it
 *    a field's KEY     a model's specs are stored UNDER that key, so renaming
 *                      it orphans every value already saved. The input is
 *                      disabled once the field exists; add a new one instead.
 *
 *  EDITS ARE LOCAL UNTIL SAVED. The whole spec goes in one PUT, because the
 *  server validates it as a whole — a duplicate key is only visible across
 *  fields — and because a half-applied spec is a form that has lost a
 *  section.
 */
export function SpecEditor({ kind }: { kind: string }) {
  const q = useProductSpec(kind);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SpecGroup[] | null>(null);
  const [saved, setSaved] = useState(0);

  // Load the server's copy into the draft once it arrives, and again whenever
  // the product changes. Keyed on `kind` so switching products cannot leave
  // the previous one's fields on screen — the D-84 failure, one level down.
  useEffect(() => { setDraft(null); }, [kind]);
  const spec = draft ?? q.data?.spec ?? [];

  const save = useMutation({
    mutationFn: () => products.setSpec(kind, spec),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.spec(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      // The Asset Catalog renders its model form from this, so its cached
      // templates are stale the moment this lands.
      void qc.invalidateQueries({ queryKey: ["asset-catalog"] });
      setDraft(null);
      setSaved((n) => n + 1);
    },
  });

  const edit = (next: SpecGroup[]) => setDraft(next);
  const dirty = draft !== null;

  if (q.isLoading) return <div className="panel panel-pad cap">Loading…</div>;

  return (
    <div>
      {/* Label on its own line, chips beneath — the pattern every other
          section-label follows. It was a label INSIDE the flex row with
          `style={{ margin: 0 }}`, which `test_section_label_spacing_is_declared_
          not_retyped` refuses: the class owns the rhythm, and an inline margin
          is that decision retyped where nobody will find it again. */}
      <div className="section-label tight">Spec fields</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <Chip tone="mute">
          {spec.reduce((n, g) => n + g.fields.length, 0)} in {spec.length} group
          {spec.length === 1 ? "" : "s"}
        </Chip>
        {dirty && <Chip tone="warn">unsaved</Chip>}
        {!dirty && saved > 0 && <Chip tone="ok">saved</Chip>}
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className="btn ghost sm"
            onClick={() => edit([...spec, { group: "New group", fields: [] }])}>
            Add group
          </button>
          <button className="btn teal sm" disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save spec"}
          </button>
          {dirty && (
            <button className="btn ghost sm" onClick={() => setDraft(null)}>
              Discard
            </button>
          )}
        </span>
      </div>

      <div className="cap" style={{ marginBottom: 8 }}>
        This is the form the Asset Catalog shows when somebody adds a model of
        this product. A product with no fields offers an empty form, which is
        why a new one stays a draft until it has some.
      </div>

      {spec.length === 0 && (
        <div className="panel panel-pad cap">
          No fields yet — this product cannot describe a model. Add a group to
          start.
        </div>
      )}

      {spec.map((g, gi) => (
        <Group key={gi} group={g}
          existing={new Set((q.data?.spec ?? []).flatMap((x) => x.fields.map((f) => f.k)))}
          onChange={(next) => edit(spec.map((x, i) => (i === gi ? next : x)))}
          onRemove={() => edit(spec.filter((_, i) => i !== gi))} />
      ))}

      {save.isError && (
        <div className="panel panel-pad" style={{ marginTop: 8 }}>
          {(save.error as Error).message}
        </div>
      )}
    </div>
  );
}

function Group({ group, existing, onChange, onRemove }: {
  group: SpecGroup;
  /** Keys already SAVED — those cannot be renamed. */
  existing: Set<string>;
  onChange: (g: SpecGroup) => void;
  onRemove: () => void;
}) {
  const setField = (i: number, f: SpecField) =>
    onChange({ ...group, fields: group.fields.map((x, j) => (j === i ? f : x)) });

  return (
    <div className="panel panel-pad" style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <SearchInput value={group.group} placeholder="Group name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...group, group: e.target.value })} />
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className="btn ghost sm" onClick={() => onChange({
            ...group,
            fields: [...group.fields, { k: "", label: "", type: "text" }],
          })}>Add field</button>
          <button className="btn ghost sm" onClick={onRemove}>Remove group</button>
        </span>
      </div>

      {group.fields.length === 0 && (
        <div className="cap" style={{ marginTop: 6 }}>
          A group with no fields is a heading with nothing under it — the
          server refuses it.
        </div>
      )}

      {group.fields.map((f, i) => (
        <Field key={i} field={f} locked={existing.has(f.k)}
          onChange={(next) => setField(i, next)}
          onRemove={() => onChange({
            ...group, fields: group.fields.filter((_, j) => j !== i),
          })} />
      ))}
    </div>
  );
}

function Field({ field, locked, onChange, onRemove }: {
  field: SpecField;
  /** Already saved, so its KEY is permanent: a model's specs are stored
   *  under it and renaming orphans every value. */
  locked: boolean;
  onChange: (f: SpecField) => void;
  onRemove: () => void;
}) {
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap",
      marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--line)",
    }}>
      <ScopeField label="Key"
        hint={locked ? "permanent — specs are stored under it" : "lower case, no spaces"}>
        <SearchInput value={field.k} mono disabled={locked} style={{ width: 130 }}
          placeholder="nom_v"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...field, k: e.target.value.toLowerCase().replace(/\s+/g, "_") })} />
      </ScopeField>
      <ScopeField label="Label">
        <SearchInput value={field.label} placeholder="Nominal voltage"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...field, label: e.target.value })} />
      </ScopeField>
      <ScopeField label="Type">
        <Select value={field.type}
          onChange={(e) => onChange({ ...field, type: e.target.value as SpecField["type"] })}>
          <option value="text">Text</option>
          <option value="num">Number</option>
          <option value="select">Choice</option>
          <option value="bool">Yes / no</option>
          <option value="date">Date</option>
        </Select>
      </ScopeField>
      {field.type === "select" && (
        <ScopeField label="Options" hint="comma separated — a choice with none is refused">
          <SearchInput value={(field.opts ?? []).join(", ")} placeholder="LFP, NMC, LTO"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({
              ...field,
              opts: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
            })} />
        </ScopeField>
      )}
      {field.type === "num" && (
        <ScopeField label="Unit">
          <SearchInput value={field.unit ?? ""} placeholder="V" style={{ width: 80 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ ...field, unit: e.target.value || undefined })} />
        </ScopeField>
      )}
      <ScopeField label="Required">
        <label style={{ display: "flex", alignItems: "center", height: 30 }}>
          <input type="checkbox" checked={!!field.req}
            onChange={(e) => onChange({ ...field, req: e.target.checked || undefined })} />
        </label>
      </ScopeField>
      <ScopeAction>
        <button className="btn ghost sm" onClick={onRemove}>Remove</button>
      </ScopeAction>
    </div>
  );
}
